import React, {useEffect, useState} from 'react';
import {Box, Grid, Typography} from '@mui/material';
import {DoorFront as DoorFrontIcon, Phone as PhoneIcon, Key as KeyIcon} from '@mui/icons-material';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {getMultipleDevicesStatistics} from 'src/apis/autoGenerate/services';
import {useNavigate} from 'react-router-dom';
import type {IntercomDeviceLogDeviceStatisticsItem} from 'src/apis/autoGenerate/types/intercomDeviceLog';
import {EntioCard} from 'src/components/ui';
import {brand, semantic} from 'src/theme';

// Types for the API response
interface DoorAccessBreakdown {
  tenantGranted: number;
  managerGranted: number;
  roomPinAccess: number;
  deliveryAccess: number;
  guestAccess: number;
  other: number;
}

interface DoorAccess {
  total: number;
  breakdown: DoorAccessBreakdown;
}

interface CallsBreakdown {
  tenantCalls: number;
  managerCalls: number;
  other: number;
}

interface Calls {
  total: number;
  breakdown: CallsBreakdown;
}

interface DailyActivity {
  date: string;
  count: number;
  operation_type: 'DOOR_ACCESS' | 'INTERCOM_CALL' | 'UNKNOWN';
}

interface EventSummaryData {
  totalActivities: number;
  doorAccess: DoorAccess;
  calls: Calls;
  dailyActivity: DailyActivity[];
}

interface EventSummaryCardProps {
  data?: EventSummaryData;
  loading?: boolean;
  deviceIds?: string; // 新增：设备ID参数
}

// Default data for when no props are provided
const defaultData: EventSummaryData = {
  totalActivities: 0,
  doorAccess: {
    total: 0,
    breakdown: {
      tenantGranted: 0,
      managerGranted: 0,
      roomPinAccess: 0,
      deliveryAccess: 0,
      guestAccess: 0,
      other: 0,
    },
  },
  calls: {
    total: 0,
    breakdown: {
      tenantCalls: 0,
      managerCalls: 0,
      other: 0,
    },
  },
  dailyActivity: [],
};

export default function EventSummaryCard({
  data,
  loading: externalLoading,
  deviceIds,
}: EventSummaryCardProps) {
  const account = useAppSelector(selectAccount);
  const [internalData, setInternalData] = useState<EventSummaryData>(defaultData);
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // 如果没有提供data，则从API获取数据
  const shouldFetchData = !data && deviceIds;
  const isLoading = externalLoading || internalLoading;
  const displayData = data || internalData;

  useEffect(() => {
    if (!shouldFetchData || !deviceIds || !account.role?.org_id) {
      return;
    }

    const fetchEventSummary = async () => {
      setInternalLoading(true);
      setError(null);

      try {
        // 只传递 deviceIds 参数，不传递 startDate 和 endDate
        const response = await getMultipleDevicesStatistics(deviceIds, undefined, undefined);

        // 检查响应格式 - 添加更严格的检查
        if (!response?.data) {
          throw new Error('Invalid API response: missing data property');
        }

        // 检查响应数据结构
        const responseData = response.data;
        if (!Array.isArray(responseData)) {
          // 如果响应不是数组，可能是包装在data属性中
          if (responseData.data && Array.isArray(responseData.data)) {
            // 数据在 response.data.data 中
          } else {
            throw new Error('Invalid API response format: expected array of device statistics');
          }
        }

        // 获取设备数据数组
        const deviceItems = Array.isArray(responseData) ? responseData : responseData.data || [];

        if (!Array.isArray(deviceItems)) {
          throw new Error('Device items is not an array');
        }

        // 聚合所有设备的数据
        const aggregatedData: EventSummaryData = {
          totalActivities: 0,
          doorAccess: {
            total: 0,
            breakdown: {
              tenantGranted: 0,
              managerGranted: 0,
              roomPinAccess: 0,
              deliveryAccess: 0,
              guestAccess: 0,
              other: 0,
            },
          },
          calls: {
            total: 0,
            breakdown: {
              tenantCalls: 0,
              managerCalls: 0,
              other: 0,
            },
          },
          dailyActivity: [],
        };

        // 处理每个设备的统计数据
        deviceItems.forEach((deviceItem: IntercomDeviceLogDeviceStatisticsItem) => {
          if (deviceItem.statistics && !deviceItem.error) {
            const stats = deviceItem.statistics;

            // 累加总活动数
            aggregatedData.totalActivities += stats.totalActivities || 0;

            // 累加门禁数据
            if (stats.doorAccess) {
              aggregatedData.doorAccess.total += stats.doorAccess.total || 0;
              if (stats.doorAccess.breakdown) {
                aggregatedData.doorAccess.breakdown.tenantGranted +=
                  stats.doorAccess.breakdown.tenantGranted || 0;
                aggregatedData.doorAccess.breakdown.managerGranted +=
                  stats.doorAccess.breakdown.managerGranted || 0;
                aggregatedData.doorAccess.breakdown.roomPinAccess +=
                  stats.doorAccess.breakdown.roomPinAccess || 0;
                aggregatedData.doorAccess.breakdown.deliveryAccess +=
                  stats.doorAccess.breakdown.deliveryAccess || 0;
                aggregatedData.doorAccess.breakdown.guestAccess +=
                  stats.doorAccess.breakdown.guestAccess || 0;
                aggregatedData.doorAccess.breakdown.other += stats.doorAccess.breakdown.other || 0;
              }
            }

            // 累加通话数据
            if (stats.calls) {
              aggregatedData.calls.total += stats.calls.total || 0;
              if (stats.calls.breakdown) {
                aggregatedData.calls.breakdown.tenantCalls +=
                  stats.calls.breakdown.tenantCalls || 0;
                aggregatedData.calls.breakdown.managerCalls +=
                  stats.calls.breakdown.managerCalls || 0;
                aggregatedData.calls.breakdown.other += stats.calls.breakdown.other || 0;
              }
            }

            // 合并日常活动数据（按日期和操作类型分组）
            if (Array.isArray(stats.dailyActivity)) {
              stats.dailyActivity.forEach(activity => {
                const existingActivity = aggregatedData.dailyActivity.find(
                  item =>
                    item.date === activity.date && item.operation_type === activity.operation_type
                );

                if (existingActivity) {
                  existingActivity.count += activity.count || 0;
                } else {
                  aggregatedData.dailyActivity.push({
                    date: activity.date,
                    count: activity.count || 0,
                    operation_type: activity.operation_type as
                      | 'DOOR_ACCESS'
                      | 'INTERCOM_CALL'
                      | 'UNKNOWN',
                  });
                }
              });
            }
          }
        });

        setInternalData(aggregatedData);
      } catch (err) {
        console.error('Failed to fetch event summary:', err);
        setError(err instanceof Error ? err.message : 'Failed to load event summary data');
      } finally {
        setInternalLoading(false);
      }
    };

    fetchEventSummary();
  }, [shouldFetchData, deviceIds, account.role?.org_id]);

  // Transform API data to display format with click handlers
  const events = [
    {
      icon: <DoorFrontIcon />,
      label: 'Door Access',
      count: displayData.doorAccess.total,
      color: brand[500],
      bgColor: brand[100],
      onClick: () => navigate('/eventSearch/doorEvent'),
    },
    {
      icon: <PhoneIcon />,
      label: 'Intercom Calls',
      count: displayData.calls.total,
      color: semantic.success.main,
      bgColor: semantic.success.light,
      onClick: () => navigate('/eventSearch/callEvent'),
    },
    {
      icon: <KeyIcon />,
      label: 'Total Activities',
      count: displayData.totalActivities,
      color: semantic.info.main,
      bgColor: semantic.info.light,
      onClick: () => navigate('/eventSearch'),
    },
  ];

  if (isLoading) {
    return (
      <EntioCard title="Events Summary">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 120,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading events...
          </Typography>
        </Box>
      </EntioCard>
    );
  }

  if (error) {
    return (
      <EntioCard title="Events Summary">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 120,
          }}
        >
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      </EntioCard>
    );
  }

  return (
    <EntioCard title="Events Summary">
      <Grid
        container
        spacing={{xs: 1, sm: 2}}
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
          {events.map((event, index) => (
            <Grid
              item
              xs={4}
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                onClick={event.onClick}
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: 'fit-content',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: {xs: 40, sm: 48},
                    height: {xs: 40, sm: 48},
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: event.bgColor,
                    color: event.color,
                    mb: 1,
                    '& svg': {
                      fontSize: {xs: 20, sm: 24},
                    },
                  }}
                >
                  {event.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    fontSize: {xs: '1rem', sm: '1.25rem'},
                    lineHeight: 1.2,
                  }}
                >
                  {event.count}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: {xs: '0.75rem', sm: '0.8rem'},
                    whiteSpace: 'nowrap',
                  }}
                >
                  {event.label}
                </Typography>
              </Box>
            </Grid>
          ))}
      </Grid>
    </EntioCard>
  );
}
